import React, { useState, Suspense } from 'react';
import {
  Box,
  Typography,
  Paper,
  useTheme,
  IconButton,
  Button,
  Tooltip,
  Alert,
} from '@mui/material';
import ModalMAIQ from 'app/components/ModalMAIQ';
import TabsMAIQ from 'app/components/TabsMAIQ';
import CloseIcon from '@mui/icons-material/Close';
import DownloadIcon from '@mui/icons-material/Download';
import GetAppIcon from '@mui/icons-material/GetApp';
import { header } from 'utils/auth';
import { formatToDealerTime } from 'utils/dateUtils';
import DownloadModalMAIQ from 'app/components/DownloadModalMAIQ';
import { ActionsAccordionMAIQ } from 'app/components/ActionsAccordionMAIQ';

interface RunDetails {
  configuration_id: string;
  properties: {
    status: boolean;
    dormant_lead_enabled: boolean;
    live_lead_enabled: boolean;
    mode: string;
    max_leads: number;
    dealer_id: string;
    details: {
      grant_type: string;
      client_id: string;
      client_secret: string;
      scope: string;
      subscription_id: string;
    };
    crm_name: string;
    enable_data_pull_api_option: boolean;
    two_fa_enabled: boolean;
    two_fa_type: string;
    two_fa_mobile: string;
    provider_name: string;
  };
  ingestion_type: string;
}

interface RunQuery {
  fieldname: string;
  originalname: string;
  encoding: string;
  mimetype: string;
  destination: string;
  filename: string;
  path: string;
  size: number;
}

interface RunExecutionDetails {
  fieldname: string;
  originalname: string;
  encoding: string;
  mimetype: string;
  destination: string;
  filename: string;
  path: string;
  size: number;
  start_date: string;
  end_date: string;
  dealer_name: string;
  dealer_id: string;
  crmName: string;
  crmSettingsId: string;
  lead_type: string;
  _id: string;
  run_id: string;
  email: string | null;
  bucketName: string;
  ingestion_type: string;
  metrics: {
    no_of_records: number;
    normalized_documents: number;
  };
}

interface OldRunExecutionDetails {
  message: string;
  stack_trace: {
    timestamp: string;
    statusCode: number;
    actionName: string;
    type: string;
  };
}

// New interface for the simplified run log structure
interface SimpleRunLog {
  _id: string;
  name: string;
  status: string;
  run_type: string;
  run_by: string;
  run_status_message?: string;
  created_at: string;
  updated_at: string;
  actions?: Array<{
    _id: string;
    function_name: string;
    parameters: any;
    status: string;
    status_history: Array<{
      status: string;
      timestamp: string;
      reason?: string;
      _id: string;
    }>;
    metadata: any;
    is_data_available_to_download: boolean;
  }>;
}

interface RunLogDetailsModalProps {
  open: boolean;
  onClose: () => void;
  runDetails: RunDetails;
  runQuery: RunQuery;
  runExecutionDetails: RunExecutionDetails | OldRunExecutionDetails;
  run_metrics?: {
    no_of_records: number;
    normalized_documents: number;
  };
  actions?: Array<{
    _id: string;
    function_name: string;
    parameters: any;
    status: string;
    status_history: Array<{
      status: string;
      timestamp: string;
      reason?: string;
      _id: string;
    }>;
    metadata: any;
    is_data_available_to_download: boolean;
  }>;
}

// New props interface for simplified modal
interface SimpleRunLogDetailsModalProps {
  open: boolean;
  onClose: () => void;
  runLog: SimpleRunLog;
}

// Type guard to check for new structure
function isNewExecutionDetails(details: any): details is RunExecutionDetails {
  return details && 'fieldname' in details;
}

// Simplified modal component for new API structure
const SimpleRunLogDetailsModal: React.FC<SimpleRunLogDetailsModalProps> = ({
  open,
  onClose,
  runLog,
}) => {
  const theme = useTheme();
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [downloadTitle, setDownloadTitle] = useState('');

  const resolveDownloadFormat = (action: any) => {
    const dataType = String(
      action?.data_type ||
        action?.parameters?.data_type ||
        action?.metadata?.dataType ||
        '',
    ).toLowerCase();
    return dataType === 'zip' ? 'zip' : 'csv';
  };

  const handleDownload = (runId?: string, actionIdParam?: string, action?: any, format?: string) => {
    
    try {
      const finalRunId = runId || runLog._id;
      const finalActionId = actionIdParam || '';
      const finalAction = action || null;
      const selectedFormat = resolveDownloadFormat(finalAction);

      console.log('Final values after processing:', { finalRunId, finalActionId, finalAction, selectedFormat });

      if (!finalRunId) {
        console.error('❌ Missing runId for download');
        alert('Error: Missing run ID. Cannot download file.');
        return;
      }

      if (!finalActionId) {
        console.error('❌ Missing actionId for download');
        alert('Error: Missing action ID. Cannot download file.');
        return;
      }

      if (!finalAction) {
        console.error('❌ Missing action data for download');
        alert('Error: Missing action information. Cannot download file.');
        return;
      }


      // Extract file information from action parameters
      const parameters = finalAction.parameters || {};
      // Create proper filename following the pattern: run_name-(action_id-or-order-or-function_name).ext
      const runName = runLog.name || 'run';
      const currentActionId = finalActionId;
      const functionName = finalAction.function_name || 'action';
      
      // Clean the run name and function name for filename compatibility
      const cleanRunName = runName.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_');
      const cleanFunctionName = functionName.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_');
      
      // Use function name as identifier, fallback to action ID
      const identifier = cleanFunctionName || currentActionId;
      const fileExtension = selectedFormat === 'zip' ? 'zip' : 'csv';
      const downloadFileName = `${cleanRunName}-(${identifier}).${fileExtension}`;

      // Create download URL with proper format using selectedFormat
      const apiPath = `/api/v2/run-v2/${finalRunId}/actions/${finalActionId}/download?format=${selectedFormat}`;
      const downloadUrl = `${process.env.REACT_APP_API_URL}${apiPath}`;

    
      setDownloadUrl(downloadUrl);
      setFileName(downloadFileName);
      setDownloadTitle(`Download ${finalAction.function_name} Data`);
      setDownloadModalOpen(true);
      
    } catch (error) {
      alert('Error setting up download. Please try again.');
    }
  };

  return (
    <>
      <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton={true}>
        <Paper
          elevation={0}
          sx={{
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              backgroundColor: '#0F2A52',
              color: 'white',
              zIndex: 10,
              flexShrink: 0,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Typography variant="h6" fontWeight="600">
                Run Log Details - {runLog.name}
              </Typography>
              <IconButton
                onClick={onClose}
                sx={{
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              p: 3,
            }}
          >
            {runLog.run_status_message ? (
              <Alert
                severity="warning"
                sx={{
                  mb: 3,
                  borderRadius: 2,
                  alignItems: 'flex-start',
                  '& .MuiAlert-message': {
                    width: '100%',
                  },
                }}
              >
                <Typography
                  variant="subtitle2"
                  sx={{ fontWeight: 700, color: '#0F2A52', mb: 0.5 }}
                >
                  Run Status Message
                </Typography>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {runLog.run_status_message}
                </Typography>
              </Alert>
            ) : null}

            {runLog.actions && runLog.actions.length > 0 ? (
              <Box>
                <Suspense fallback={<Typography>Loading actions...</Typography>}>
                  <ActionsAccordionMAIQ
                    actions={runLog.actions}
                    runId={runLog._id}
                    onDownload={handleDownload}
                  />
                </Suspense>
              </Box>
            ) : (
              <Box sx={{ p: 3, textAlign: 'center' }}>
                <Typography variant="h6" color="text.secondary">
                  No actions found for this run log
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  Run ID: {runLog._id}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Status: {runLog.status}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Type: {runLog.run_type}
                </Typography>
              </Box>
            )}
          </Box>
        </Paper>
      </ModalMAIQ>

      {/* Download Modal */}
      <DownloadModalMAIQ
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        downloadUrl={downloadUrl}
        title={downloadTitle}
        fileName={fileName}
      />
    </>
  );
};

const RunLogDetailsModal: React.FC<RunLogDetailsModalProps> = ({
  open,
  onClose,
  runDetails,
  runQuery,
  runExecutionDetails,
  run_metrics,
  actions,
}) => {
  const theme = useTheme();
  const [downloadModalOpen, setDownloadModalOpen] = useState(false);
  const [downloadOutputModalOpen, setDownloadOutputModalOpen] = useState(false);
  const [downloadCleanModalOpen, setDownloadCleanModalOpen] = useState(false);
  const [downloadUrl, setDownloadUrl] = useState('');
  const [fileName, setFileName] = useState('');
  const [downloadTitle, setDownloadTitle] = useState('');

  const resolveDownloadFormat = (action: any) => {
    const dataType = String(
      action?.data_type ||
        action?.parameters?.data_type ||
        action?.metadata?.dataType ||
        '',
    ).toLowerCase();
    return dataType === 'zip' ? 'zip' : 'csv';
  };

  const renderRunDetails = () => (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="subtitle1"
        sx={{ mb: 2, color: '#0F2A52', fontWeight: 600 }}
      >
        Configuration Details
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {runDetails?.configuration_id !== undefined && (
          <Typography>
            <strong>Configuration ID:</strong> {runDetails.configuration_id}
          </Typography>
        )}
        {runDetails?.ingestion_type !== undefined && (
          <Typography>
            <strong>Ingestion Type:</strong> {runDetails.ingestion_type}
          </Typography>
        )}
        {runDetails?.properties?.status !== undefined && (
          <Typography>
            <strong>Status:</strong>{' '}
            {runDetails.properties.status ? 'Active' : 'Inactive'}
          </Typography>
        )}
        {runDetails?.properties?.mode !== undefined && (
          <Typography>
            <strong>Mode:</strong> {runDetails.properties.mode}
          </Typography>
        )}
        {runDetails?.properties?.max_leads !== undefined && (
          <Typography>
            <strong>Max Leads:</strong> {runDetails.properties.max_leads}
          </Typography>
        )}
        {runDetails?.properties?.crm_name !== undefined && (
          <Typography>
            <strong>CRM Name:</strong> {runDetails.properties.crm_name}
          </Typography>
        )}
        {runDetails?.properties?.provider_name !== undefined && (
          <Typography>
            <strong>Provider:</strong> {runDetails.properties.provider_name}
          </Typography>
        )}
      </Box>

      <Typography
        variant="subtitle1"
        sx={{ mt: 3, mb: 2, color: '#0F2A52', fontWeight: 600 }}
      >
        API Details
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {runDetails?.properties?.details?.grant_type !== undefined && (
          <Typography>
            <strong>Grant Type:</strong>{' '}
            {runDetails.properties.details.grant_type}
          </Typography>
        )}
        {runDetails?.properties?.details?.client_id !== undefined && (
          <Typography>
            <strong>Client ID:</strong>{' '}
            {runDetails.properties.details.client_id}
          </Typography>
        )}
        {runDetails?.properties?.details?.scope !== undefined && (
          <Typography>
            <strong>Scope:</strong> {runDetails.properties.details.scope}
          </Typography>
        )}
        {runDetails?.properties?.details?.subscription_id !== undefined && (
          <Typography>
            <strong>Subscription ID:</strong>{' '}
            {runDetails.properties.details.subscription_id}
          </Typography>
        )}
      </Box>

      <Typography
        variant="subtitle1"
        sx={{ mt: 3, mb: 2, color: '#0F2A52', fontWeight: 600 }}
      >
        Security Settings
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {runDetails?.properties?.two_fa_enabled !== undefined && (
          <Typography>
            <strong>2FA Enabled:</strong>{' '}
            {runDetails.properties.two_fa_enabled ? 'Yes' : 'No'}
          </Typography>
        )}
        {runDetails?.properties?.two_fa_type !== undefined && (
          <Typography>
            <strong>2FA Type:</strong> {runDetails.properties.two_fa_type}
          </Typography>
        )}
        {runDetails?.properties?.two_fa_mobile !== undefined && (
          <Typography>
            <strong>2FA Mobile:</strong> {runDetails.properties.two_fa_mobile}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderRunQuery = () => (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="subtitle1"
        sx={{ mb: 2, color: '#0F2A52', fontWeight: 600 }}
      >
        File Details
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {runQuery?.originalname !== undefined && (
          <Typography>
            <strong>Original Name:</strong> {runQuery.originalname}
          </Typography>
        )}
        {runQuery?.size !== undefined && (
          <Typography>
            <strong>File Size:</strong> {(runQuery.size / 1024).toFixed(2)} KB
          </Typography>
        )}
        {runQuery?.mimetype !== undefined && (
          <Typography>
            <strong>MIME Type:</strong> {runQuery.mimetype}
          </Typography>
        )}
        {runQuery?.encoding !== undefined && (
          <Typography>
            <strong>Encoding:</strong> {runQuery.encoding}
          </Typography>
        )}
        {runQuery?.destination !== undefined && (
          <Typography>
            <strong>Destination:</strong> {runQuery.destination}
          </Typography>
        )}
        {runQuery?.path !== undefined && (
          <Typography>
            <strong>Path:</strong> {runQuery.path}
          </Typography>
        )}
      </Box>
    </Box>
  );

  const renderRunExecutionDetails = () => {
    if (isNewExecutionDetails(runExecutionDetails)) {
      // New structure UI
      type ExecutionDetailField = { label: string; key: string; render?: (v: any) => any };
      const newFields: ExecutionDetailField[] = [
        { label: 'Dealer Name', key: 'dealer_name' },
        { label: 'Dealer ID', key: 'dealer_id' },
        { label: 'CRM Name', key: 'crmName' },
        { label: 'CRM Settings ID', key: 'crmSettingsId' },
        { label: 'Lead Type', key: 'lead_type' },
        { label: 'Run ID', key: 'run_id' },
        {
          label: 'Start Date',
          key: 'start_date',
          render: (v: string) => (v ? formatToDealerTime(v) : undefined),
        },
        {
          label: 'End Date',
          key: 'end_date',
          render: (v: string) => (v ? formatToDealerTime(v) : undefined),
        },
        { label: 'Ingestion Type', key: 'ingestion_type' },
        { label: 'Bucket Name', key: 'bucketName' },
        { label: 'Email', key: 'email' },
        { label: 'File Name', key: 'filename' },
        {
          label: 'File Size',
          key: 'size',
          render: (v: number) =>
            v ? (v / 1024).toFixed(2) + ' KB' : undefined,
        },
        { label: 'MIME Type', key: 'mimetype' },
        { label: 'Encoding', key: 'encoding' },
        { label: 'Destination', key: 'destination' },
        { label: 'Path', key: 'path' },
        {
          label: 'No. of Records',
          key: 'metrics.no_of_records',
          render: (v: number) => v,
        },
        {
          label: 'Normalized Documents',
          key: 'metrics.normalized_documents',
          render: (v: number) => v,
        },
      ];
      return (
        <Box sx={{ p: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{ mb: 2, color: '#0F2A52', fontWeight: 600 }}
          >
            Execution Details
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
            {newFields.map(field => {
              let value = field.key.includes('.')
                ? field.key
                    .split('.')
                    .reduce((acc, k) => acc?.[k], runExecutionDetails)
                : runExecutionDetails[field.key];
              if (field.render) value = field.render(value);
              if (value === undefined || value === '' || value === null)
                return null;
              return (
                <Typography key={field.key}>
                  <strong>{field.label}:</strong> {value}
                </Typography>
              );
            })}
          </Box>
        </Box>
      );
    } else {
      const oldDetails = runExecutionDetails as OldRunExecutionDetails;
      const oldFields = [
        { label: 'Error Message', key: 'message' },
        {
          label: 'Timestamp',
          key: 'stack_trace.timestamp',
          render: (v: string) => (v ? formatToDealerTime(v) : undefined),
        },
        { label: 'Status Code', key: 'stack_trace.statusCode' },
        { label: 'Action Name', key: 'stack_trace.actionName' },
        { label: 'Error Type', key: 'stack_trace.type' },
      ];
      return (
        <Box sx={{ p: 2 }}>
          <Typography
            variant="subtitle1"
            sx={{ mb: 2, color: '#0F2A52', fontWeight: 600 }}
          >
            Error Details
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr', gap: 2 }}>
            {oldFields.map(field => {
              let value = field.key.includes('.')
                ? field.key.split('.').reduce((acc, k) => acc?.[k], oldDetails)
                : oldDetails[field.key];
              if ((field as { render?: (v: any) => any }).render) value = (field as { render: (v: any) => any }).render(value);
              if (value === undefined || value === '' || value === null)
                return null;
              return (
                <Typography key={field.key}>
                  <strong>{field.label}:</strong> {value}
                </Typography>
              );
            })}
          </Box>
        </Box>
      );
    }
  };

  // Run Metrics Tab
  const renderRunMetrics = () => (
    <Box sx={{ p: 2 }}>
      <Typography
        variant="subtitle1"
        sx={{ mb: 2, color: '#0F2A52', fontWeight: 600 }}
      >
        Run Metrics
      </Typography>
      <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
        {run_metrics?.no_of_records !== undefined && (
          <Typography>
            <strong>No. of Records:</strong> {run_metrics.no_of_records}
          </Typography>
        )}
        {run_metrics?.normalized_documents !== undefined && (
          <Typography>
            <strong>Normalized Documents:</strong>{' '}
            {run_metrics.normalized_documents}
          </Typography>
        )}
      </Box>
    </Box>
  );

  // Function to handle downloading original data
  const handleDownload = (runId?: string, actionIdParam?: string, action?: any, format?: string) => {
    console.log('=== DOWNLOAD PROCESS STARTED ===');
    console.log('handleDownload called with parameters:', { runId, actionId: actionIdParam, action });
    console.log('runExecutionDetails:', runExecutionDetails);
    console.log('isNewExecutionDetails result:', isNewExecutionDetails(runExecutionDetails));
    
    try {
      // Use provided runId and actionId, or fallback to execution details
      const finalRunId = runId || (isNewExecutionDetails(runExecutionDetails) ? runExecutionDetails.run_id : '');
      const finalActionId = actionIdParam || '';
      const finalAction = action || null;
      const selectedFormat = resolveDownloadFormat(finalAction);

      console.log('Final values after processing:', { finalRunId, finalActionId, finalAction, selectedFormat });

      // Validate required data
      if (!finalRunId) {
        console.error('❌ Missing runId for download');
        console.error('runId parameter:', runId);
        console.error('runExecutionDetails.run_id:', isNewExecutionDetails(runExecutionDetails) ? runExecutionDetails.run_id : 'N/A');
        alert('Error: Missing run ID. Cannot download file.');
        return;
      }

      if (!finalActionId) {
        console.error('❌ Missing actionId for download');
        console.error('actionId parameter:', actionIdParam);
        alert('Error: Missing action ID. Cannot download file.');
        return;
      }

      console.log('✅ Validation passed - both runId and actionId are present');

      // Extract file information from action parameters if available
      let downloadFileName = 'download.csv';
      let fileExtension = selectedFormat === 'zip' ? 'zip' : 'csv';
      
      if (finalAction && finalAction.parameters) {
        const parameters = finalAction.parameters;
        // Create proper filename following the pattern: run_name-(action_id-or-order-or-function_name).ext
        // Try to get run name from execution details first, then fallback to dealer_id or default
        const runName = isNewExecutionDetails(runExecutionDetails) 
          ? runExecutionDetails.dealer_name || runExecutionDetails.dealer_id || 'run'
          : runDetails?.properties?.dealer_id || 'run';
        const currentActionId = finalActionId;
        const functionName = finalAction.function_name || 'action';
        
        // Clean the run name and function name for filename compatibility
        const cleanRunName = runName.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_');
        const cleanFunctionName = functionName.replace(/[^a-zA-Z0-9_-]/g, '_').replace(/_+/g, '_');
        
        // Use function name as identifier, fallback to action ID
        const identifier = cleanFunctionName || currentActionId;
        fileExtension = selectedFormat === 'zip' ? 'zip' : 'csv';
        downloadFileName = `${cleanRunName}-(${identifier}).${fileExtension}`;
      }

      // Create URL for download using the new API path with proper format
      const apiPath = `/api/v2/run-v2/${finalRunId}/actions/${finalActionId}/download?format=${selectedFormat}`;
      const downloadUrl = `${process.env.REACT_APP_API_URL}${apiPath}`;

      // Set download modal properties
      setDownloadUrl(downloadUrl);
      setFileName(downloadFileName);
      setDownloadTitle(finalAction ? `Download ${finalAction.function_name} Data` : 'Download Original Data');
      setDownloadModalOpen(true);
      
    } catch (error) {
      alert('Error setting up download. Please try again.');
    }
  };

  // Helper to check if an object has at least one non-empty value
  const hasData = (obj: any) => {
    if (!obj) return false;
    return Object.values(obj as object).some(
      v =>
        v !== undefined &&
        v !== null &&
        v !== '' &&
        !(typeof v === 'object' && Object.keys(v).length === 0),
    );
  };

  // Dynamically build tabs
  const tabs: { label: string; content: JSX.Element }[] = [];
  if (hasData(runDetails)) {
    tabs.push({ label: 'Run Details', content: renderRunDetails() });
  }
  if (hasData(runQuery)) {
    tabs.push({ label: 'Run Query', content: renderRunQuery() });
  }
  if (hasData(runExecutionDetails)) {
    tabs.push({
      label: 'Execution Details',
      content: renderRunExecutionDetails(),
    });
  }
  if (hasData(run_metrics)) {
    tabs.push({ label: 'Run Metrics', content: renderRunMetrics() });
  }
  if (actions && actions.length > 0) {
    tabs.push({ 
      label: 'Actions', 
      content: (
        <Box sx={{ p: 2 }}>
          <Suspense fallback={<Typography>Loading actions...</Typography>}>
              <ActionsAccordionMAIQ 
              actions={actions} 
              onDownload={handleDownload}
            />
          </Suspense>
        </Box>
      )
    });
  }

  // Function to handle downloading output data
  // const handleDownloadOutputData = () => {
  //   try {
  //     // Get dealer_id from runDetails or from execution details
  //     const dealer_id =
  //       runDetails.properties.dealer_id ||
  //       (isNewExecutionDetails(runExecutionDetails)
  //         ? runExecutionDetails.dealer_id
  //         : '');

  //     // Get _id and bucketName from execution details when available
  //     let fileId = isNewExecutionDetails(runExecutionDetails)
  //       ? runExecutionDetails.run_id
  //       : '';

  //     // Validate required data
  //     if (!dealer_id) {
  //       console.error('Missing dealer_id for download');
  //       alert('Error: Missing dealer information. Cannot download file.');
  //       return;
  //     }

  //     if (!fileId) {
  //       console.error('Missing file ID for download');
  //       alert('Error: Missing file information. Cannot download file.');
  //       return;
  //     }

  //     // Create URL for download using the complete backend URL (not frontend)
  //     const apiPath = `/api/v2/data-downloads/normalized_lead/csv?run_id=${fileId}&dealer_id=${dealer_id}`;
  //     const downloadUrl = `${process.env.REACT_APP_API_URL}${apiPath}`;

  //     // Set download modal properties
  //     setDownloadUrl(downloadUrl);
  //     setDownloadTitle('Download Output Data');
  //     setDownloadOutputModalOpen(true);
  //   } catch (error) {
  //     console.error('Error setting up download:', error);
  //     alert('Error setting up download. Please try again.');
  //   }
  // };

  // // Function to handle downloading clean data
  // const handleDownloadCleanData = () => {
  //   try {
  //     // Get dealer_id from runDetails or from execution details
  //     const dealer_id =
  //       runDetails.properties.dealer_id ||
  //       (isNewExecutionDetails(runExecutionDetails)
  //         ? runExecutionDetails.dealer_id
  //         : '');

  //     // Get _id and bucketName from execution details when available
  //     let fileId = isNewExecutionDetails(runExecutionDetails)
  //       ? runExecutionDetails.run_id
  //       : '';

  //     // Validate required data
  //     if (!dealer_id) {
  //       console.error('Missing dealer_id for download');
  //       alert('Error: Missing dealer information. Cannot download file.');
  //       return;
  //     }

  //     if (!fileId) {
  //       console.error('Missing file ID for download');
  //       alert('Error: Missing file information. Cannot download file.');
  //       return;
  //     }

  //     // Create URL for download using the complete backend URL (not frontend)
  //     const apiPath = `/api/v2/data-downloads/clean_lead/csv?run_id=${fileId}&dealer_id=${dealer_id}`;
  //     const downloadUrl = `${process.env.REACT_APP_API_URL}${apiPath}`;

  //     // Set download modal properties
  //     setDownloadUrl(downloadUrl);
  //     setDownloadTitle('Download Clean Data');
  //     setDownloadCleanModalOpen(true);
  //   } catch (error) {
  //     console.error('Error setting up download:', error);
  //     alert('Error setting up download. Please try again.');
  //   }
  // };

  return (
    <>
      <ModalMAIQ open={open} onClose={onClose} hideDefaultCloseButton={true}>
        <Paper
          elevation={0}
          sx={{
            width: '800px',
            maxWidth: '90vw',
            maxHeight: '85vh',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 0,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              p: 2,
              backgroundColor: '#0F2A52',
              color: 'white',
              zIndex: 10,
              flexShrink: 0,
            }}
          >
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              width="100%"
            >
              <Typography variant="h6" fontWeight="600">
                Run Log Details
              </Typography>
              <IconButton
                onClick={onClose}
                sx={{
                  color: 'white',
                  '&:hover': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                  },
                }}
              >
                <CloseIcon />
              </IconButton>
            </Box>
          </Box>

          {/* Content */}
          <Box
            sx={{
              flex: 1,
              overflow: 'auto',
              display: 'flex',
              flexDirection: 'column',
              p: 3,
            }}
          >
            {actions && actions.length > 0 ? (
              <Suspense fallback={<Typography>Loading actions...</Typography>}>
                <ActionsAccordionMAIQ 
                  actions={actions} 
                  onDownload={handleDownload}
                />
              </Suspense>
            ) : (
              <TabsMAIQ
                tabs={tabs}
                contentStyle={{
                  border: '1px solid rgba(0, 0, 0, 0.05)',
                  borderRadius: '4px',
                  mt: 1,
                }}
              />
            )}
          </Box>

          {/* Download Buttons */}
          {/* <Box
            sx={{
              p: 3,
              borderTop: '1px solid',
              borderColor: 'divider',
              display: 'flex',
              justifyContent: 'space-between',
              gap: 2,
              width: '100%',
            }}
          >
            <Tooltip title="Download Original Data">
              <Button
                variant="outlined"
                startIcon={<GetAppIcon />}
                fullWidth
                size="large"
                onClick={handleDownloadOriginalData}
                sx={{
                  color: '#0F2A52',
                  borderColor: '#0F2A52',
                  py: 1.5,
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: 'rgba(15, 42, 82, 0.04)',
                    borderColor: '#0F2A52',
                  },
                }}
              >
                Original Data
              </Button>
            </Tooltip>
            <Tooltip title="Download Clean Data">
              <Button
                variant="outlined"
                startIcon={<DownloadIcon />}
                fullWidth
                size="large"
                onClick={handleDownloadCleanData}
                sx={{
                  color: '#0F2A52',
                  borderColor: '#0F2A52',
                  py: 1.5,
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: 'rgba(15, 42, 82, 0.04)',
                    borderColor: '#0F2A52',
                  },
                }}
              >
                Clean Data
              </Button>
            </Tooltip>
            <Tooltip title="Download Output Data">
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                fullWidth
                size="large"
                onClick={handleDownloadOutputData}
                sx={{
                  backgroundColor: '#0F2A52',
                  color: 'white',
                  py: 1.5,
                  height: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  '&:hover': {
                    backgroundColor: '#0A1F3D',
                  },
                }}
              >
                Output Data
              </Button>
            </Tooltip>
          </Box> */}
        </Paper>
      </ModalMAIQ>

      {/* Download Original Data Modal */}
      <DownloadModalMAIQ
        open={downloadModalOpen}
        onClose={() => setDownloadModalOpen(false)}
        downloadUrl={downloadUrl}
        title={downloadTitle}
        fileName={fileName}
      />

      {/* Download Output Data Modal */}
      <DownloadModalMAIQ
        open={downloadOutputModalOpen}
        onClose={() => setDownloadOutputModalOpen(false)}
        downloadUrl={downloadUrl}
        title={downloadTitle}
        fileName={fileName}
      />

      {/* Download Clean Data Modal */}
      <DownloadModalMAIQ
        open={downloadCleanModalOpen}
        onClose={() => setDownloadCleanModalOpen(false)}
        downloadUrl={downloadUrl}
        title={downloadTitle}
        fileName={fileName}
      />
    </>
  );
};

export { SimpleRunLogDetailsModal };
export default RunLogDetailsModal;
